import {FC, useEffect, useState} from 'react'
import Searchbar from 'app/components/Searchbar'
import Button from 'app/components/Button'
import {toAbsoluteUrl} from '_metronic/helpers'
import {
  Timestamp,
  doc,
  collection,
  addDoc,
  updateDoc,
  serverTimestamp,
  deleteDoc,
} from '@firebase/firestore'
import Input from 'app/components/Input'
import Textarea from 'app/components/Textarea'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {useAuth} from 'app/modules/auth'
import db from 'services/firebase'
import {showSuccessToast} from 'app/components/ShowToast'
import {format} from 'date-fns'
import AddSubscriberModal from '../components/modals/AddSubscriberModal'
import ConfirmationModal from 'app/components/ConfirmationModal'
import {ToastContainer} from 'react-toastify'
import CustomDrawer from '../components/Drawer'

type Props = {
  addedUpdates: UpdateDataType[]
  subscriberList: SubscriberDataType[]
}

export type UpdateDataType = {
  id: string
  subject: string
  content: string
  createdAt: Timestamp
}

export type SubscriberDataType = {
  id: string
  email: string
  subscribedOn: Timestamp
}

const validationSchema = Yup.object().shape({
  subject: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(100, 'Maximum 100 characters')
    .required('Email subject is required'),
  content: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(800, 'Maximum 800 characters')
    .required('Email content is required'),
})

const UpdatesTab: FC<Props> = ({addedUpdates, subscriberList}) => {
  const {currentUser} = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [emailSearchTerm, setEmailSearchTerm] = useState('')
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isSubscriberListDrawerOpen, setIsSubscriberListDrawerOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [updateSearchLength, setUpdateSearchLength] = useState(false)
  const [subscriberSearchLength, setSubscriberSearchLength] = useState(false)

  const [toDeleteSubscriber, setToDeleteSubscriber] = useState<SubscriberDataType>()

  const [filteredUpdates, setFilteredUpdates] = useState<UpdateDataType[]>(addedUpdates)
  const [filteredSubscriberList, setFilteredSubscriberList] =
    useState<SubscriberDataType[]>(subscriberList)

  useEffect(() => {
    setFilteredUpdates(addedUpdates)
  }, [addedUpdates])

  useEffect(() => {
    setFilteredSubscriberList(subscriberList)
  }, [subscriberList])

  useEffect(() => {
    if (addedUpdates.length > 0) {
      let filteredData = addedUpdates.filter((update) =>
        update.subject.toLowerCase().includes(searchTerm.toLowerCase())
      )

      setFilteredUpdates(filteredData)
      setUpdateSearchLength(filteredData.length === 0)
    }
  }, [searchTerm, addedUpdates])

  useEffect(() => {
    if (subscriberList.length > 0) {
      let filteredData = subscriberList.filter((subscriber) =>
        subscriber.email.toLowerCase().includes(emailSearchTerm.toLowerCase())
      )

      setFilteredSubscriberList(filteredData)
      setSubscriberSearchLength(filteredData.length === 0)
    }
  }, [emailSearchTerm, subscriberList])

  const formik = useFormik<UpdateDataType>({
    initialValues: {id: '', subject: '', content: '', createdAt: Timestamp.fromDate(new Date())},
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        const tenantId = currentUser?.tenantId || ''
        const updatesRef = collection(
          db,
          'tenants',
          tenantId,
          'trust_center',
          'updates',
          'all_updates'
        )

        const newDocRef = await addDoc(updatesRef, {
          subject: values.subject,
          content: values.content,
          createdAt: Timestamp.fromDate(new Date()),
        })

        await updateDoc(newDocRef, {
          id: newDocRef.id,
        })

        showSuccessToast({message: 'New update for trust center has been added successfully'})

        const currentDate = new Date()
        const year = currentDate.getFullYear()
        const month = currentDate.getMonth() + 1
        const day = currentDate.getDate()
        const todaysDate = month + '/' + day + '/' + year
        const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
        await addDoc(activitiesRef, {
          message: `${values?.subject} subject update for trust center has been added`,
          timestamp: serverTimestamp(),
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          username: currentUser?.userName,
          date: todaysDate,
          email: currentUser?.email,
          target: 'Trust Center',
          action: 'Update added',
        })
      } catch (error) {
        console.error('Error while adding update for trust center')
      } finally {
        setLoading(false)
        setIsDrawerOpen(false)
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''
        formik.resetForm()
      }
    },
  })

  const onCancel = () => {
    setIsDrawerOpen(false)
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
    formik.resetForm()
  }

  const onSubscriberListDrawerCancel = () => {
    setIsSubscriberListDrawerOpen(false)
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
  }

  const handleRemoveSubscriber = async () => {
    setLoading(true)
    try {
      const tenantId = currentUser?.tenantId || ''
      const subscribersRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'subscribers',
        'all_subscribers'
      )

      await deleteDoc(doc(subscribersRef, toDeleteSubscriber?.id))

      const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const day = currentDate.getDate()
      const todaysDate = month + '/' + day + '/' + year

      await addDoc(activitiesRef, {
        message: `${toDeleteSubscriber?.email} subscriber email for trust center has been removed`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        username: currentUser?.userName,
        date: todaysDate,
        email: currentUser?.email,
        target: 'Trust Center',
        action: 'Subscriber removed',
      })

      showSuccessToast({message: 'Subscriber has been removed successfully'})
    } catch (error) {
      console.error('Error while removing framework: ', error)
    } finally {
      setLoading(false)
      setToDeleteSubscriber(undefined)
      setIsDeleteModalOpen(false)
    }
  }

  return (
    <div>
      <CustomDrawer
        isDrawerOpen={isDrawerOpen}
        onCancel={onCancel}
        title='Add Trust Center Updates'
        submitHandler={() => formik.submitForm()}
        submitBtnText='Add Trust Center Updates'
        submitBtnLoadingText='Adding...'
      >
        <div style={{padding: '20px'}}>
          <p className='text-dark mb-6' style={{fontSize: '13px'}}>
            Let your existing and potential customers know what has changed since the last Trust
            Center update. All subscribers will receive an email update.
          </p>

          <div className='form' onSubmit={() => formik.submitForm()}>
            <div className='form-group mb-5'>
              <Input
                type='text'
                label='Email Subject'
                placeholder='Subject of your email'
                error={formik.touched.subject && formik.errors.subject ? formik.errors.subject : ''}
                {...formik.getFieldProps('subject')}
              />
            </div>

            <div className='form-group'>
              <Textarea
                label='Email Content'
                placeholder='Type your email content...'
                error={formik.touched.content && formik.errors.content ? formik.errors.content : ''}
                {...formik.getFieldProps('content')}
              />
            </div>
          </div>
        </div>
      </CustomDrawer>

      <CustomDrawer
        isDrawerOpen={isSubscriberListDrawerOpen}
        onCancel={onSubscriberListDrawerCancel}
        title='Subscriber List'
      >
        <div style={{padding: '20px'}}>
          <p className='text-dark mb-6' style={{fontSize: '13px'}}>
            Here are the list of people who has opted to receive your Trust Center updates via
            email.
          </p>

          <div className='d-flex flex-column'>
            <div className='d-flex justify-content-between align-items-center border-bottom border-gray-300 pb-1 mb-5'>
              <span className='text-dark fs-6 fw-bold'>Subscribers</span>

              <Button
                btnType='transparent'
                onClick={() => {
                  setIsModalOpen(true)
                }}
              >
                <img
                  alt='plus-icon'
                  src={toAbsoluteUrl('/media/icon/plus.svg')}
                  className='me-2'
                  style={{height: '18px', width: '18px'}}
                />
                <span>Add Subscriber</span>
              </Button>
            </div>

            <div className='flex-full mb-5'>
              <Searchbar searchTerm={emailSearchTerm} setSearchTerm={setEmailSearchTerm} width='100%' />
            </div>

            <div
              className='table-responsive'
              style={{
                borderRadius: '6.175px',
                border: '1px solid var(--bs-border-color)',
                maxHeight: '55vh',
                overflowY: 'auto',
              }}
            >
              <table className='table table-rounded gy-7 gs-7 mb-0 align-middle'>
                <thead>
                  <tr
                    className='fw-bold'
                    style={{
                      color: '#5A5A5A',
                      boxShadow: 'inset 0 -1px 0 var(--bs-border-color)',
                      backgroundColor: '#F9FAFB',
                      position: 'sticky',
                      top: 0,
                      zIndex: '10',
                    }}
                  >
                    <th className='min-w-200px'>EMAIL</th>
                    <th className='min-w-200px'>SUBSCRIBED ON</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredSubscriberList.length > 0 ? (
                    filteredSubscriberList.map((subscriber, index) => (
                      <tr
                        style={{
                          borderBottom: `${
                            index !== filteredSubscriberList.length - 1
                              ? '1px solid var(--bs-border-color)'
                              : ''
                          }`,
                        }}
                        key={subscriber.id}
                      >
                        <td>
                          <span className='text-dark fs-6 fw-medium'>{subscriber.email}</span>
                        </td>

                        <td>
                          <div className='d-flex align-items-center justify-content-between'>
                            <span className='text-dark fs-6'>
                              {format(
                                subscriber.subscribedOn.toDate(),
                                "MMMM dd, yyyy 'at' h:mm:ss a"
                              )}
                            </span>

                            <Button
                              style={{
                                height: '34px',
                                width: '34px',
                              }}
                              onClick={() => {
                                setIsDeleteModalOpen(true)
                                setToDeleteSubscriber(subscriber)
                              }}
                            >
                              <img
                                alt='trash-icon'
                                src={toAbsoluteUrl('/media/icon/trash.svg')}
                                className=''
                                style={{height: '18px', width: '18px'}}
                              />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2}>
                        <div
                          className='w-100 d-flex align-items-center justify-content-center'
                          style={{height: '245px'}}
                        >
                          {subscriberSearchLength ? (
                            <div className='d-flex flex-column align-items-center gap-4'>
                              <img src='/media/buttons/emptystate.svg' alt='' />
                              <p className='mb-0' style={{fontWeight: '600'}}>
                                No subscribers found
                              </p>
                              <p>
                                We couldn't find any subscribers that match your search. Maybe try a
                                different search!
                              </p>
                            </div>
                          ) : (
                            <div
                              className='w-100 d-flex justify-content-center align-items-center'
                              style={{height: '100%'}}
                            >
                              <div className='d-flex flex-column align-items-center gap-4'>
                                <div
                                  className='d-flex align-items-center justify-content-center'
                                  style={{
                                    backgroundColor: '#F3F4F6',
                                    borderRadius: '12px',
                                    height: '48px',
                                    width: '48px',
                                  }}
                                >
                                  <img
                                    alt='users-icon'
                                    src={toAbsoluteUrl('/media/icon/users.svg')}
                                    className=''
                                    style={{height: '32px', width: '32px'}}
                                  />
                                </div>

                                <span className='text-dark fs-6 fw-medium'>
                                  No subscribers added
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </CustomDrawer>

      {isModalOpen && (
        <AddSubscriberModal isModalOpen={isModalOpen} hideModal={() => setIsModalOpen(false)} />
      )}

      {isDeleteModalOpen && (
        <ConfirmationModal
          isModalOpen={isDeleteModalOpen}
          hideModal={() => setIsDeleteModalOpen(false)}
          confirmBtnClickHandler={handleRemoveSubscriber}
          toDeleteItemTitle='subscriber'
          isLoading={loading}
        />
      )}

      <div
        className='d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between gap-5 gap-sm-0'
        style={{marginBottom: '32px'}}
      >
        <div>
          <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        </div>

        <div className='d-flex align-items-center' style={{gap: '12px'}}>
          <Button
            className='flex-fill flex-sm-none'
            onClick={() => {
              setIsSubscriberListDrawerOpen(true)
              const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
              document.body.style.overflow = 'hidden'
              document.body.style.paddingRight = `${scrollBarWidth}px`
            }}
          >
            <img
              alt='bell-ringing-icon'
              src={toAbsoluteUrl('/media/icon/bell-ringing.svg')}
              className='me-2'
              style={{height: '18px', width: '18px'}}
            />
            <span>Subscriber List</span>
          </Button>

          <Button
            className='flex-fill flex-sm-none'
            onClick={() => {
              setIsDrawerOpen(true)
              const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
              document.body.style.overflow = 'hidden'
              document.body.style.paddingRight = `${scrollBarWidth}px`
            }}
          >
            <img
              alt='plus-icon'
              src={toAbsoluteUrl('/media/icon/plus.svg')}
              className='me-2'
              style={{height: '18px', width: '18px'}}
            />
            <span>Add Updates</span>
          </Button>
        </div>
      </div>

      {filteredUpdates.length > 0 ? (
        <div className='d-flex flex-column' style={{gap: '24px'}}>
          {filteredUpdates.map((update) => (
            <div
              key={update.id}
              className='d-flex flex-column'
              style={{
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                gap: '4px',
                padding: '20px',
              }}
            >
              <span className='text-secondary-gray fs-7 fw-bold'>
                {format(update.createdAt.toDate(), 'd MMMM, yyyy')}
              </span>

              <h4 className='text-dark fs-5 fw-bold mb-0'>{update.subject}</h4>

              <p className='text-dark mb-0' style={{fontSize: '14px'}}>
                {update.content}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div
          className='w-100 d-flex align-items-center justify-content-center'
          style={{height: '350px'}}
        >
          {updateSearchLength ? (
            <div className='d-flex flex-column align-items-center gap-4' style={{height: '100%'}}>
              <img src={toAbsoluteUrl('/media/buttons/emptystate.svg')} alt='empty-state-image' />
              <p className='mb-0' style={{fontWeight: '600'}}>
                No updates found
              </p>
              <p>
                We couldn't find any updates that match your search. Maybe try a different search!
              </p>
            </div>
          ) : (
            <div
              className='w-100 d-flex justify-content-center align-items-center'
              style={{height: '100%'}}
            >
              <div className='d-flex flex-column align-items-center gap-4'>
                <div
                  className='d-flex align-items-center justify-content-center'
                  style={{
                    backgroundColor: '#F3F4F6',
                    borderRadius: '12px',
                    height: '48px',
                    width: '48px',
                  }}
                >
                  <img
                    alt='speakerphone-icon'
                    src={toAbsoluteUrl('/media/icon/speakerphone.svg')}
                    className=''
                    style={{height: '32px', width: '32px'}}
                  />
                </div>

                <span className='text-dark fs-6 fw-medium'>No trust center updates added</span>
              </div>
            </div>
          )}
        </div>
      )}

      <ToastContainer />
    </div>
  )
}

export default UpdatesTab
